import { BackToTop, DateSelector } from 'components/common';
import { ComposableFilters } from 'components/common/ComposableFilters';
import { SegmentSelector } from 'components/common/SegmentSelector';
import { ExportIcon, ExternalLinkIcon } from 'components/icons';
import {
  RelationsContextProvider,
  RelationsGrid,
  useProductRelations,
} from 'components/products/relations';
import { Skeleton, SuggestionCallout } from 'components/ui';
import { format } from 'date-fns';
import { lazy, Suspense, useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from 'store';
import { userDetailsState } from 'store/userDetailsSlice';
import { usePageTitle } from 'utils';

const ExportMenuLazy = lazy(
  () => import('components/products/ProductsExportMenu')
);

export default function ProductRelationsPage() {
  usePageTitle('Product relations | Segments');

  const exportContainerRef = useRef<HTMLDivElement>(null);

  return (
    <RelationsContextProvider>
      <div className="max-w-screen-xl px-5 mx-auto md:pl-segments-72px md:pr-20 py-11">
        <div className="flex items-center space-x-2">
          <p className="font-bold leading-6">Products</p>
        </div>
        <h1 className="mt-4 font-bold leading-10 text-segments-44px">
          Product relations
        </h1>
        <p className="mt-6 text-base">
          Increase your sales by finding the right timing and product to
          promote.
        </p>
        <a
          href="https://help.tresl.co/en/articles/5395988-product-relations"
          className="flex items-start space-x-0.5 text-segments-link font-bold mt-2"
          target="_blank"
          rel="noreferrer"
        >
          <p>Learn more</p>
          <ExternalLinkIcon className="inline w-5 h-5 align-middle" />
        </a>
        <div className="relative flex items-center space-x-3 mt-7">
          <div className="w-full pl-5 pr-6 text-base border h-segments-60px bg-segments-accent-green-3 rounded-2xl border-">
            <div className="flex items-center justify-between h-full align-middle">
              <SegmentSelector />
              <ComposableFilters />
            </div>
          </div>
          <div className="2xl:flex-shrink-0">
            <DateSelector defaultPeriod="Last 365 days" />
          </div>
        </div>
        <p className="mt-1.5  text-segments-13px  text-black text-opacity-50">
          Important: the product relations has the following default settings:
          Financial status, Subtotal status and Order date.
        </p>
        <ExportHeader exportContainerRef={exportContainerRef} />
        <div className="py-2" ref={exportContainerRef}>
          <Summary />
          <div className="mt-6 font-bold text-center md:hidden">
            Module no available on mobile
          </div>
          <div className="hidden mt-8 md:block">
            <RelationsGrid />
          </div>
        </div>
        <BackToTop />
      </div>
    </RelationsContextProvider>
  );
}

function Summary() {
  const { currentStore } = useAppSelector(userDetailsState);

  const { relationsSummary, relationsSummaryIsLoading } = useProductRelations();

  const locale = currentStore.completeInfo?.primary_locale ?? 'en';

  const SummaryStr = useMemo(() => {
    if (!!!relationsSummary?.summary_text || relationsSummaryIsLoading)
      return 'Loading...';

    const splitStr = relationsSummary?.summary_text.split(' ');

    return splitStr?.map((subStr, index) => {
      const numberPattern = /\d+/g;
      const isNumber = subStr.match(numberPattern);

      if (isNumber) {
        return (
          <span className="font-bold" key={index}>
            {' '}
            {Number(subStr).toLocaleString(locale)}{' '}
          </span>
        );
      }
      return ` ${subStr} `;
    });
  }, [relationsSummaryIsLoading, relationsSummary?.summary_text, locale]);

  return (
    <SuggestionCallout>
      <div className="px-6 py-4">
        <p className="text-black text-opacity-60">Summary</p>
        <p className="mt-2">{SummaryStr}</p>
      </div>
    </SuggestionCallout>
  );
}

function ExportHeader({
  exportContainerRef,
}: {
  exportContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const { selectedProduct, productList, crossSell, replenishment } =
    useProductRelations();

  const { currentStore } = useAppSelector(userDetailsState);

  const locale = currentStore.completeInfo?.primary_locale ?? 'en';
  const currency = currentStore.completeInfo?.currency ?? 'USD';

  const generateCSVData = useCallback(() => {
    const columns = [
      'product_name',
      'product_customers',
      'product_aclv',
      'cross_sale_product_1',
      'cross_sale_rate_product_1',
      'cross_sale_product_2',
      'cross_sale_rate_product_2',
      'cross_sale_product_3',
      'cross_sale_rate_product_3',
      'cross_sale_product_4',
      'cross_sale_rate_product_4',
      'replenishment_customer',
      'replenishment_rate',
      'replenishment_median_days',
      'replenishment_most_before',
      'replenishment_week_0_customer',
      'replenishment_week_1_customer',
      'replenishment_week_2_customer',
      'replenishment_week_3_customer',
      'replenishment_week_4_customer',
      'replenishment_week_5_customer',
      'replenishment_week_6_customer',
      'replenishment_week_7_customer',
      'replenishment_week_8_customer',
      'replenishment_week_9_customer',
      'replenishment_week_10_customer',
      'replenishment_week_11_customer',
      'replenishment_week_12_customer',
      'replenishment_week_13_customer',
      'replenishment_week_14_customer',
      'replenishment_week_15_customer',
      'replenishment_week_16_customer',
    ];

    const csvHeaders = columns.map((column) => ({
      label: column,
      key: column,
    }));

    let csvData: any[] = [];

    if (productList && productList.length > 0 && !!!selectedProduct) {
      csvData = productList.map((product) => ({
        product_name: product.title,
        product_customers: product.unique_customers.toLocaleString(),
        product_aclv: product.aclv?.toLocaleString(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          style: 'currency',
          currency,
        }),
      }));
    } else if (selectedProduct) {
      csvData = [
        {
          product_name: selectedProduct.title,
          product_customers: selectedProduct.unique_customers.toLocaleString(),
          product_aclv: selectedProduct.aclv?.toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            style: 'currency',
            currency,
          }),

          ...Object.fromEntries(
            crossSell?.flatMap((product, index) => [
              [`cross_sale_product_${index + 1}`, product.title],
              [
                `cross_sale_rate_product_${index + 1}`,
                product.likely_to_buy_rate?.toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  style: 'percent',
                }),
              ],
            ]) || []
          ),
          replenishment_customer: replenishment?.bought_once?.toLocaleString(),
          replenishment_rate: replenishment?.replenishment_rate?.toLocaleString(
            locale,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              style: 'percent',
            }
          ),
          replenishment_median_days: replenishment?.median?.toLocaleString(
            locale,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          ),
          replenishment_most_before: replenishment?.most_before?.toLocaleString(
            locale,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          ),
          ...Object.fromEntries(
            replenishment?.histogram.flatMap((item, index) => [
              [
                `replenishment_week_${index}_customer`,
                item.count.toLocaleString(),
              ],
            ]) || []
          ),
        },
      ];
    }

    return {
      headers: csvHeaders,
      values: csvData,
    };
  }, [
    crossSell,
    currency,
    locale,
    productList,
    replenishment?.bought_once,
    replenishment?.histogram,
    replenishment?.median,
    replenishment?.most_before,
    replenishment?.replenishment_rate,
    selectedProduct,
  ]);

  const filename = `${
    currentStore.completeInfo?.store_name
  }-product_relations-${format(Date.now(), 'yyyyMMdd')}`;

  return (
    <div className="flex justify-end mt-4 md:mt-10">
      <Suspense
        fallback={
          <div className="p-3 rounded-full bg-segments-gray focus:outline-none">
            <ExportIcon className="w-5 h-5" />
          </div>
        }
      >
        <Skeleton isLoaded={Boolean(productList && productList.length > 0)}>
          <ExportMenuLazy
            containerRef={exportContainerRef}
            generateCSVData={generateCSVData}
            filename={filename}
          />
        </Skeleton>
      </Suspense>
    </div>
  );
}
